import { useCallback, useMemo, useRef, useState } from 'react';
import type { BetaDataTableContextValue } from './Context';

export function useRowSelection(): BetaDataTableContextValue {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [contextMenuCoords, setContextMenuCoords] = useState<
    [number, number] | null
  >(null);

  const selectableRef = useRef(new Set<string>());
  const hasRegisteredRef = useRef(false);
  const [selectableRowIds, setSelectableRowIds] = useState<string[]>([]);

  const selectedSet = useMemo(() => new Set(selectedRows), [selectedRows]);

  const checkRow = useCallback((id: string | string[]) => {
    const idsToAdd = Array.isArray(id) ? id : [id];
    const filtered = hasRegisteredRef.current
      ? idsToAdd.filter((rowId) => selectableRef.current.has(rowId))
      : idsToAdd;
    if (filtered.length === 0) return;
    setSelectedRows((prev) => [...new Set([...prev, ...filtered])]);
  }, []);

  const uncheckRow = useCallback((id: string | string[]) => {
    const idsToRemove = new Set(Array.isArray(id) ? id : [id]);
    setSelectedRows((prev) => prev.filter((rowId) => !idsToRemove.has(rowId)));
  }, []);

  const isChecked = useCallback(
    (id: string) => selectedSet.has(id),
    [selectedSet]
  );

  const clearSelection = useCallback(() => setSelectedRows([]), []);

  const registerSelectableRow = useCallback((id: string) => {
    if (selectableRef.current.has(id)) return;
    hasRegisteredRef.current = true;
    selectableRef.current.add(id);
    setSelectableRowIds(Array.from(selectableRef.current));
  }, []);

  const unregisterSelectableRow = useCallback((id: string) => {
    if (!selectableRef.current.has(id)) return;
    selectableRef.current.delete(id);
    setSelectableRowIds(Array.from(selectableRef.current));
    setSelectedRows((prev) => prev.filter((rowId) => rowId !== id));
  }, []);

  return {
    checkRow,
    clearSelection,
    isChecked,
    registerSelectableRow,
    selectableRowIds,
    selectedRows,
    setSelectedRows,
    uncheckRow,
    unregisterSelectableRow,
    contextMenuCoords,
    setContextMenuCoords,
  };
}
